import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import { sendPasswordChangedEmail } from '../utils/sendEmail';
import './ProfilePage.css';
import '../styles/userPages.css';

const IcCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

function PasswordChangeForm({ email, firstName }) {
  const [step, setStep]                       = useState('idle');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword]               = useState('');
  const [confirm, setConfirm]                 = useState('');
  const [nonce, setNonce]                     = useState('');
  const [loading, setLoading]                 = useState(false);
  const [success, setSuccess]                 = useState(false);
  const [error, setError]                     = useState('');
  const { t } = useLanguage();

  const resetState = () => {
    setStep('idle');
    setCurrentPassword('');
    setPassword('');
    setConfirm('');
    setNonce('');
    setError('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.reauthenticate();
      if (err) {
        setError(err.message || t('settings.password.reauthFailed'));
      } else {
        setStep('verify');
      }
    } catch (e) {
      setError(t('settings.password.reauthFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!currentPassword)            { setError(t('settings.password.currentPasswordRequired')); return; }
    if (!nonce.trim())               { setError(t('settings.password.codeRequired')); return; }
    if (password.length < 6)         { setError(t('settings.password.minLength')); return; }
    if (password !== confirm)        { setError(t('settings.password.noMatch')); return; }
    if (password === currentPassword) {
      setError(t('settings.password.mustBeDifferent'));
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({
        password,
        current_password: currentPassword,
        nonce: nonce.trim(),
      });
      if (err) {
        const msg = (err.message || '').toLowerCase();
        const code = (err.code || '').toLowerCase();
        if (
          code === 'invalid_credentials' ||
          code === 'current_password_required' ||
          msg.includes('current password') ||
          msg.includes('current_password') ||
          msg.includes('invalid login') ||
          msg.includes('invalid credentials')
        ) {
          setError(t('settings.password.wrongCurrentPassword'));
        } else if (msg.includes('nonce') || msg.includes('otp') || msg.includes('reauthentication') || msg.includes('expired') || msg.includes('code')) {
          setError(t('settings.password.invalidCode'));
        } else if (msg.includes('session')) {
          setError(t('settings.password.sessionExpired'));
        } else if (msg.includes('weak') || msg.includes('strength') || msg.includes('pwned')) {
          setError(t('settings.password.tooWeak'));
        } else if (msg.includes('same') || msg.includes('different from the old')) {
          setError(t('settings.password.mustBeDifferent'));
        } else {
          setError(err.message);
        }
      } else {
        try {
          const changedAt = new Date().toLocaleString('en-GB', {
            day: '2-digit', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
          });
          await sendPasswordChangedEmail({ email, firstName, changedAt });
        } catch (mailErr) {
          console.error('[SettingsPage] password-changed email failed:', mailErr);
        }
        setSuccess(true);
        resetState();
        setTimeout(() => setSuccess(false), 4000);
      }
    } catch (e) {
      setError(t('settings.password.error'));
    } finally {
      setLoading(false);
    }
  };

  const codeSentParts = t('settings.password.codeSent').split('{email}');
  const resetIntroParts = t('settings.password.resetIntro').split('{email}');

  return (
    <div>
      {step === 'idle' && (
        <form onSubmit={handleSendCode}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
            {t('settings.password.resetTitle')}
          </h3>
          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.6 }}>
            {resetIntroParts[0]}<strong>{email}</strong>{resetIntroParts[1]}
          </p>
          {success && (
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#10B981', display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {t('settings.password.updated')}
            </div>
          )}
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#ef4444' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            className="profile-page__btn"
            style={{ width: 'auto', padding: '10px 22px', marginTop: 4 }}
            disabled={loading}
          >
            {loading ? t('settings.password.sending') : t('settings.password.getCodeBtn')}
          </button>
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={handleSubmit}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>
            {t('settings.password.resetTitle')}
          </h3>
          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.6 }}>
            {codeSentParts[0]}<strong>{email}</strong>{codeSentParts[1]}
          </p>
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#ef4444' }}>
              {error}
            </div>
          )}
          <div className="profile-page__field">
            <label>{t('settings.password.currentPassword')}</label>
            <input
              type="password"
              autoComplete="current-password"
              placeholder={t('settings.password.currentPasswordPlaceholder')}
              value={currentPassword}
              onChange={e => { setCurrentPassword(e.target.value); setError(''); }}
            />
          </div>
          <div className="profile-page__field" style={{ marginTop: 12 }}>
            <label>{t('settings.password.code')}</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              placeholder={t('settings.password.codePlaceholder')}
              value={nonce}
              onChange={e => { setNonce(e.target.value.replace(/\D/g, '')); setError(''); }}
            />
          </div>
          <div className="profile-page__field" style={{ marginTop: 12 }}>
            <label>{t('settings.password.newPassword')}</label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder={t('settings.password.placeholder')}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
            />
          </div>
          <div className="profile-page__field" style={{ marginTop: 12 }}>
            <label>{t('settings.password.confirmPassword')}</label>
            <input
              type="password"
              autoComplete="new-password"
              placeholder={t('settings.password.confirmPlaceholder')}
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setError(''); }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              type="submit"
              className="profile-page__btn"
              style={{ width: 'auto', padding: '10px 22px' }}
              disabled={loading}
            >
              {loading ? t('settings.password.updating') : t('settings.password.updateBtn')}
            </button>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={loading}
              style={{ background: 'none', border: 'none', color: '#B8924A', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              {t('settings.password.resendCode')}
            </button>
            <button
              type="button"
              onClick={resetState}
              disabled={loading}
              style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 13, cursor: 'pointer', padding: 0 }}
            >
              {t('settings.password.cancelChange')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user, profile, logout, hasProAccess } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [notifications, setNotifications] = useState({
    notify_matching_listings: true,
    notify_price_drops:       true,
    notify_weekly_newsletter: false,
    unsubscribed_all:         false,
  });
  const [saved, setSaved]               = useState(false);
  const [saving, setSaving]             = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput]     = useState('');
  const [deleting, setDeleting]           = useState(false);

  if (!user) { navigate('/login'); return null; }

  const toggle = key => setNotifications(n => {
    if (key === 'unsubscribed_all') return { ...n, unsubscribed_all: !n.unsubscribed_all };
    return { ...n, [key]: !n[key], unsubscribed_all: false };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase.from('profiles').update({
        notify_matching_listings: notifications.notify_matching_listings,
        notify_price_drops:       notifications.notify_price_drops,
        notify_weekly_newsletter: notifications.notify_weekly_newsletter,
        unsubscribed_all:         notifications.unsubscribed_all,
      }).eq('id', user.id);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('notify_matching_listings, notify_price_drops, notify_weekly_newsletter, unsubscribed_all').eq('id', user.id).single().then(({ data }) => {
      if (data) setNotifications({
        notify_matching_listings: data.notify_matching_listings ?? true,
        notify_price_drops:       data.notify_price_drops ?? true,
        notify_weekly_newsletter: data.notify_weekly_newsletter ?? false,
        unsubscribed_all:         data.unsubscribed_all ?? false,
      });
    });
  }, [user?.id]);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const uid = user?.id;
      await supabase.from('price_drop_notifications').delete().eq('user_id', uid);
      await supabase.from('watchlist').delete().eq('user_id', uid);
      await supabase.from('email_logs').delete().eq('user_id', uid);
      await supabase.from('pdf_downloads').delete().eq('user_id', uid);
      await supabase.from('profiles').delete().eq('id', uid);
      await supabase.auth.signOut();
      navigate('/');
    } catch (e) {
      console.error('Delete account error:', e);
      setDeleting(false);
    }
  };

  const notifItems = [
    { key: 'notify_matching_listings', labelKey: 'settings.notif.newListings', descKey: 'settings.notif.newListingsDesc', proOnly: true  },
    { key: 'notify_price_drops',       labelKey: 'settings.notif.priceDrops',  descKey: 'settings.notif.priceDropsDesc',  proOnly: true  },
    { key: 'notify_weekly_newsletter', labelKey: 'settings.notif.newsletter',  descKey: 'settings.notif.newsletterDesc',  proOnly: false },
  ];

  return (
    <div className="profile-page">
      <div className="profile-page__inner">

        <div className="profile-page__sidebar">
          <div className="profile-page__avatar">
            {profile?.is_broker && profile?.partner_status === 'approved' && profile?.avatar_url
              ? <img src={profile.avatar_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : user.avatar}
          </div>
          <div className="profile-page__name">{user.firstName} {user.lastName}</div>
          <div className="profile-page__email">{user.email}</div>
          <nav className="profile-page__nav">
            <button className="profile-page__nav-item" onClick={() => navigate('/profile')}>{t('nav.myProfile')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/my-listings')}>{t('nav.myListings')}</button>
            <button className="profile-page__nav-item" onClick={() => navigate('/inbox')}>{t('nav.inbox')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--active" onClick={() => navigate('/settings')}>{t('nav.settings')}</button>
            <button className="profile-page__nav-item profile-page__nav-item--logout" onClick={() => { logout(); navigate('/'); }}>{t('nav.logout')}</button>
          </nav>
        </div>

        <div className="profile-page__content">
          <div className="profile-page__section-header">
            <h1 className="profile-page__title">{t('settings.title')}</h1>
            <p className="profile-page__sub">{t('settings.sub')}</p>
          </div>

          <div className="profile-page__card">
            <h2 className="profile-page__card-title">{t('settings.security')}</h2>
            <div className="settings__security">
              <PasswordChangeForm email={user.email} firstName={user.firstName} />
            </div>
          </div>

          <div className="profile-page__card profile-page__card--danger">
            <h2 className="profile-page__card-title">{t('settings.dangerZone')}</h2>
            <p className="settings__danger-desc">{t('settings.dangerDesc')}</p>
            {deleteConfirm ? (
              <div>
                <p style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, fontWeight: 600 }}>
                  {t('settings.confirmDelete')}
                </p>
                <input
                  type="text"
                  placeholder={t('settings.deletePlaceholder')}
                  value={deleteInput}
                  onChange={e => setDeleteInput(e.target.value)}
                  style={{ border: '1.5px solid #ef4444', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12, width: '100%', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="settings__danger-btn"
                    disabled={deleteInput !== 'DELETE' || deleting}
                    onClick={handleDeleteAccount}
                    style={{ opacity: deleteInput !== 'DELETE' ? 0.5 : 1 }}
                  >
                    {deleting ? t('settings.deleting') : t('settings.confirmDeleteBtn')}
                  </button>
                  <button
                    onClick={() => { setDeleteConfirm(false); setDeleteInput(''); }}
                    style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#6b7280' }}
                  >
                    {t('settings.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button className="settings__danger-btn" onClick={() => setDeleteConfirm(true)}>
                {t('settings.deleteBtn')}
              </button>
            )}
          </div>

          <div className="profile-page__actions">
            {saved && (
              <span className="profile-page__saved">
                <IcCheck /> {t('settings.settingsSaved')}
              </span>
            )}
            <button className="profile-page__btn" onClick={handleSave} disabled={saving}>{saving ? t('settings.saving') : t('settings.saveSettings')}</button>
          </div>
        </div>

      </div>
    </div>
  );
}
